/**
 * Icon Cache — Sippar
 *
 * Caché persistente en IndexedDB para iconos base64 de dispositivos y apps.
 *
 * Los iconos se almacenan como strings base64 y raramente cambian.
 * Evita el patrón N+1 de peticiones individuales en cada recarga de página.
 *
 * Expiración: 30 días.
 */

import { openDB, IDBPDatabase } from 'idb';
import apiClient from './apiClient';

// ─── Tipos ─────────────────────────────────────────────────────────────
interface IconEntry {
  /** Clave compuesta: `${type}:${id}` */
  key: string;
  /** ID del dispositivo o app */
  id: string;
  /** Tipo: 'device' | 'app' */
  type: 'device' | 'app';
  /** Imagen en base64 */
  base64: string;
  /** Timestamp de almacenamiento */
  timestamp: number;
}

// ─── Constantes ────────────────────────────────────────────────────────
const DB_NAME = 'sippar-icon-cache';
const DB_VERSION = 1;
const STORE_NAME = 'icons';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 días

// ─── Singleton DB ──────────────────────────────────────────────────────
let dbPromise: Promise<IDBPDatabase> | null = null;

function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
          store.createIndex('type', 'type');
          store.createIndex('timestamp', 'timestamp');
        }
      },
    });
  }
  return dbPromise;
}

// ─── Helpers internos ──────────────────────────────────────────────────

function makeKey(type: 'device' | 'app', id: string): string {
  return `${type}:${id}`;
}

function getApiEndpoint(type: 'device' | 'app', id: string): string {
  return type === 'device'
    ? `/api/device/${id}/icon`
    : `/api/apps/${id}/icon`;
}

// ─── API pública ───────────────────────────────────────────────────────

/**
 * Obtener un icono (desde caché o red).
 * Si está en IndexedDB y no ha expirado, devuelve inmediatamente sin red.
 *
 * @param id - ID del dispositivo o app
 * @param type - 'device' | 'app'
 * @returns El string base64 del icono, o undefined si no se puede obtener
 */
export async function getIcon(
  id: string,
  type: 'device' | 'app'
): Promise<string | undefined> {
  const key = makeKey(type, id);

  try {
    const db = await getDB();
    const cached = await db.get(STORE_NAME, key) as IconEntry | undefined;

    // Hit de caché fresco
    if (cached && (Date.now() - cached.timestamp) < MAX_AGE_MS) {
      return cached.base64;
    }

    // Fetch desde la API
    const base64 = await fetchIcon(id, type);
    if (base64) {
      await saveIcon(id, type, base64);
    }
    return base64;
  } catch (error) {
    console.warn(`[iconCache] Error obteniendo icono ${type}:${id}:`, error);

    // Intentar devolver datos stale si los hay
    try {
      const db = await getDB();
      const stale = await db.get(STORE_NAME, key) as IconEntry | undefined;
      if (stale) return stale.base64;
    } catch {
      // Ignorar
    }

    return undefined;
  }
}

/**
 * Obtener múltiples iconos en batch.
 * Solo hace peticiones para los que no estén en caché.
 *
 * @param ids - IDs a obtener
 * @param type - 'device' | 'app'
 * @returns Map<id, base64>
 */
export async function getIcons(
  ids: string[],
  type: 'device' | 'app'
): Promise<Record<string, string>> {
  const result: Record<string, string> = {};
  const missing: string[] = [];

  try {
    const db = await getDB();

    // Paso 1: Buscar todos en cache
    for (const id of ids) {
      const key = makeKey(type, id);
      const cached = await db.get(STORE_NAME, key) as IconEntry | undefined;

      if (cached && (Date.now() - cached.timestamp) < MAX_AGE_MS) {
        result[id] = cached.base64;
      } else {
        missing.push(id);
      }
    }

    // Paso 2: Fetch los que faltan (en paralelo, limitado a 3 concurrentes)
    if (missing.length > 0) {
      const batchSize = 3; // Limitar concurrencia por ancho de banda
      for (let i = 0; i < missing.length; i += batchSize) {
        const batch = missing.slice(i, i + batchSize);
        const results = await Promise.allSettled(
          batch.map(async (id) => {
            const base64 = await fetchIcon(id, type);
            if (base64) {
              await saveIcon(id, type, base64);
              result[id] = base64;
            }
          })
        );

        // Log errores
        results.forEach((r, idx) => {
          if (r.status === 'rejected') {
            console.warn(`[iconCache] Error fetching icon ${type}:${batch[idx]}:`, r.reason);
          }
        });
      }
    }
  } catch (error) {
    console.warn('[iconCache] Error en getIcons batch:', error);
  }

  return result;
}

/**
 * Invalidar un icono específico o todos los de un tipo.
 */
export async function invalidateIcon(id?: string, type?: 'device' | 'app'): Promise<void> {
  try {
    const db = await getDB();

    if (id && type) {
      await db.delete(STORE_NAME, makeKey(type, id));
    } else {
      // Limpiar todo
      await db.clear(STORE_NAME);
    }
  } catch (error) {
    console.warn('[iconCache] Error invalidando:', error);
  }
}

/**
 * Limpiar iconos expirados
 */
export async function cleanExpiredIcons(): Promise<void> {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    let cursor = await store.openCursor();

    let cleaned = 0;
    while (cursor) {
      const entry = cursor.value as IconEntry;
      if (Date.now() - entry.timestamp > MAX_AGE_MS) {
        await cursor.delete();
        cleaned++;
      }
      cursor = await cursor.continue();
    }

    await tx.done;
    if (cleaned > 0) {
      console.log(`[iconCache] Limpiados ${cleaned} iconos expirados`);
    }
  } catch (error) {
    console.warn('[iconCache] Error limpiando expirados:', error);
  }
}

// ─── Funciones privadas ────────────────────────────────────────────────

async function fetchIcon(id: string, type: 'device' | 'app'): Promise<string | undefined> {
  try {
    const endpoint = getApiEndpoint(type, id);
    const response = await apiClient.get(endpoint);
    // La API devuelve el string base64 directamente (o como response.data)
    const data = response.data;
    return typeof data === 'string' ? data : undefined;
  } catch (error) {
    console.warn(`[iconCache] Error fetching ${type}:${id}:`, error);
    return undefined;
  }
}

async function saveIcon(id: string, type: 'device' | 'app', base64: string): Promise<void> {
  try {
    const db = await getDB();
    const entry: IconEntry = {
      key: makeKey(type, id),
      id,
      type,
      base64,
      timestamp: Date.now(),
    };
    await db.put(STORE_NAME, entry);
  } catch (error) {
    console.warn(`[iconCache] Error guardando ${type}:${id}:`, error);
  }
}
